/**
 * 🚗 VEHICLE DISCOUNT CAMPAIGNS SERVICE
 * Manages vehicle discount campaigns for admin dashboard
 */

import { getApiBaseUrl } from '@/config/environment';


export interface VehicleCampaign {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface VehicleCampaignFormData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  adminUserId: string;
}

export interface VehicleCampaignStats {
  totalCampaigns: number;
  activeCampaigns: number;
  upcomingCampaigns: number;
  expiredCampaigns: number;
}

const vehicleCampaignService = {
  /**
   * Get all vehicle discount campaigns
   */
  async getCampaigns(filters?: {
    status?: 'active' | 'upcoming' | 'expired' | 'all';
    search?: string;
    page?: number;
    limit?: number;
  }) {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.status && filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.page !== undefined) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());

      const url = `${getApiBaseUrl()}/api/v1/admin/vehicle-discount-campaigns${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Vehicle Campaigns API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Backend returns array directly, not wrapped in campaigns object
      const campaigns = Array.isArray(result.data) ? result.data : [];

      return {
        success: result.success,
        data: {
          campaigns,
          pagination: { total: campaigns.length, page: 1, limit: 100, totalPages: 1 }
        }
      };
    } catch (error) {
      console.error('❌ Error fetching vehicle campaigns:', error);
      return {
        success: false,
        data: {
          campaigns: [],
          pagination: { total: 0, page: 1, limit: 20, totalPages: 0 }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch campaigns'
      };
    }
  },

  /**
   * Get campaign statistics
   */
  async getStats(): Promise<VehicleCampaignStats> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/vehicle-discount-campaigns/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        return result.data;
      }

      return {
        totalCampaigns: 0,
        activeCampaigns: 0,
        upcomingCampaigns: 0,
        expiredCampaigns: 0
      };
    } catch (error) {
      console.error('❌ Error fetching campaign stats:', error);
      return {
        totalCampaigns: 0,
        activeCampaigns: 0,
        upcomingCampaigns: 0,
        expiredCampaigns: 0
      };
    }
  },

  /**
   * Create new vehicle discount campaign
   */
  async createCampaign(data: VehicleCampaignFormData) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/vehicle-discount-campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Campaign API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error creating campaign:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create campaign'
      };
    }
  },

  /**
   * Update existing campaign
   */
  async updateCampaign(id: string, data: Partial<VehicleCampaignFormData>) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/vehicle-discount-campaigns/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Campaign API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error updating campaign:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update campaign'
      };
    }
  },

  /**
   * Delete campaign
   */
  async deleteCampaign(id: string) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/vehicle-discount-campaigns/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete Campaign API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error deleting campaign:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete campaign'
      };
    }
  },

  /**
   * Toggle campaign active status
   */
  async toggleCampaign(id: string, isActive: boolean, adminUserId: string) {
    return this.updateCampaign(id, { isActive, adminUserId });
  }
};

export default vehicleCampaignService;
